<template>
  <div class="coupon-bind-page">
    <h2>绑定推荐券</h2>
    <p class="lead">
      输入他人分享的推荐券号（以 <code>RC</code> 开头）。绑定成功后，将根据券类型发放相应礼遇。
      每位用户终身仅可绑定一张推荐券。
    </p>

    <el-skeleton v-if="loading && isLoggedIn" rows="3" animated />

    <template v-else>
    <el-alert
      v-if="!isLoggedIn"
      type="info"
      :closable="false"
      show-icon
      title="请先登录"
      description="绑定推荐券需要登录账号。注册时也可在注册页填写券号。"
      class="block-alert"
    >
      <el-button type="primary" size="small" @click="goLogin">去登录</el-button>
    </el-alert>

    <el-card v-else-if="bindStatus?.hasBinding" shadow="never" class="status-card">
      <h3>已绑定推荐券</h3>
      <el-descriptions :column="1" border size="small">
        <el-descriptions-item label="券号">{{ bindStatus.couponNo }}</el-descriptions-item>
        <el-descriptions-item label="类型">
          {{ couponTypeLabel(bindStatus.couponType) }}
        </el-descriptions-item>
        <el-descriptions-item v-if="bindStatus.referrerNickname" label="推荐人">
          {{ bindStatus.referrerNickname }}
        </el-descriptions-item>
      </el-descriptions>
      <div class="actions">
        <el-button @click="$router.push('/referral/earnings')">查看推荐收益</el-button>
        <el-button @click="$router.push('/referral/coupons')">我的推荐券</el-button>
      </div>
    </el-card>

    <el-card v-else shadow="never" class="bind-card">
      <h3>输入券号</h3>
      <p class="hint">注册后 7 天内可补绑（与注册时填券二选一）。</p>
      <div class="bind-row">
        <el-input
          v-model="couponNo"
          placeholder="例如 RC1A2B3C4D5E"
          maxlength="32"
          clearable
          @blur="onCheck"
          @keyup.enter="submit"
        />
        <el-button type="primary" :loading="binding" @click="submit">绑定</el-button>
      </div>
      <p v-if="checkHint" class="check-hint" :class="{ error: checkError }">{{ checkHint }}</p>
      <ul class="type-help">
        <li v-for="item in typeHelpItems" :key="item.type">
          <strong>{{ item.label }}</strong>：{{ item.hint }}
        </li>
      </ul>
    </el-card>
    </template>
  </div>
</template>

<script>
import { goToLogin, isLoggedIn } from '@/utils/loginFlow'
import {
  bindReferralCoupon,
  checkReferralCoupon,
  fetchReferralBindStatus,
  COUPON_TYPE_HINTS,
  COUPON_TYPE_LABELS,
  couponTypeLabel
} from '@/utils/referralCoupon'

export default {
  name: 'ReferralCouponBindView',
  data() {
    return {
      couponNo: '',
      checkHint: '',
      checkError: false,
      binding: false,
      bindStatus: null,
      loading: true
    }
  },
  computed: {
    isLoggedIn() {
      return isLoggedIn()
    },
    typeHelpItems() {
      return Object.keys(COUPON_TYPE_LABELS).map(type => ({
        type,
        label: COUPON_TYPE_LABELS[type],
        hint: COUPON_TYPE_HINTS[type]
      }))
    }
  },
  created() {
    const q = this.$route.query.coupon || this.$route.query.couponNo
    if (typeof q === 'string' && q.trim()) {
      this.couponNo = q.trim()
    }
    this.initPage()
  },
  methods: {
    couponTypeLabel,
    goLogin() {
      goToLogin(this.$router, this.$route.fullPath)
    },
    async initPage() {
      if (!this.isLoggedIn) {
        this.loading = false
        if (this.couponNo) {
          await this.onCheck()
        }
        return
      }
      try {
        this.bindStatus = await fetchReferralBindStatus()
        if (!this.bindStatus.hasBinding && this.couponNo) {
          await this.onCheck()
        }
      } catch (e) {
        this.$message.error(e.message || '加载失败')
      } finally {
        this.loading = false
      }
    },
    async onCheck() {
      if (!this.couponNo.trim()) {
        this.checkHint = ''
        this.checkError = false
        return
      }
      try {
        const data = await checkReferralCoupon(this.couponNo)
        if (data.valid) {
          const typeLabel = COUPON_TYPE_LABELS[data.couponType] || ''
          this.checkHint = data.message || `「${data.displayName || ''}」可绑定${typeLabel ? ` · ${typeLabel}` : ''}`
          this.checkError = false
        } else {
          this.checkHint = data.message || '推荐券无效'
          this.checkError = true
        }
      } catch {
        this.checkHint = ''
        this.checkError = false
      }
    },
    async submit() {
      if (!this.isLoggedIn) {
        this.goLogin()
        return
      }
      this.binding = true
      try {
        const res = await bindReferralCoupon(this.couponNo)
        this.$message.success(res.message || '绑定成功')
        this.bindStatus = await fetchReferralBindStatus()
        this.couponNo = ''
        this.checkHint = ''
      } catch (e) {
        this.$message.error(e.message || '绑定失败')
        await this.onCheck()
      } finally {
        this.binding = false
      }
    }
  }
}
</script>

<style scoped>
.coupon-bind-page {
  max-width: 560px;
  margin: 0 auto;
  padding: 24px 16px 48px;
}
h2 {
  margin: 0 0 8px;
}
.lead {
  color: #606266;
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0 0 24px;
}
.lead code {
  background: #f4f4f5;
  padding: 2px 6px;
  border-radius: 4px;
}
.block-alert {
  margin-bottom: 20px;
}
.status-card h3,
.bind-card h3 {
  margin: 0 0 12px;
  font-size: 1.05rem;
}
.hint {
  color: #909399;
  font-size: 13px;
  margin: 0 0 16px;
}
.bind-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}
.bind-row .el-input {
  flex: 1;
  min-width: 200px;
}
.check-hint {
  margin: 12px 0 0;
  font-size: 13px;
  color: #67c23a;
}
.check-hint.error {
  color: #f56c6c;
}
.type-help {
  margin: 20px 0 0;
  padding-left: 1.2rem;
  color: #606266;
  font-size: 13px;
  line-height: 1.7;
}
.actions {
  margin-top: 16px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}
</style>
